import React from 'react';
import { CalendarDays, Clock, User, Video } from 'lucide-react';
import { Button } from '../Button';
import type { WebinarSession } from '../../data/webinars';
import { formatSessionDate, formatSessionDay } from '../../utils/webinars';
type SessionCardProps = {
  session: WebinarSession;
  isNext?: boolean;
  isPast?: boolean;
};
export function SessionCard({
  session,
  isNext = false,
  isPast = false
}: SessionCardProps) {
  const dateLabel = formatSessionDate(session);
  const dayLabel = formatSessionDay(session);
  return <article aria-labelledby={`session-${session.id}-date`} className={['flex h-full w-full flex-col rounded-[16px] border bg-white p-8 transition duration-200', isPast ? 'border-hairline opacity-70' : 'border-hairline shadow-card hover:-translate-y-1 hover:shadow-card-hover'].join(' ')} data-id="element-44" data-mp="1z3ahdpculcey">
      <div className="flex min-h-7 items-start justify-between gap-4" data-id="element-45" data-mp="2hujt85a3cd9p">
        <p className="text-[15px] font-medium text-subtle" data-id="element-46" data-mp="2hs72p3q9oul1">{dayLabel}</p>
        {isNext && <span className="inline-flex shrink-0 items-center rounded-full bg-success-bg px-3 py-1 text-[13px] font-semibold text-success-fg" data-id="element-47" data-mp="jdj0m788tt44">
            Next up
          </span>}
        {isPast && <span className="inline-flex shrink-0 items-center rounded-full bg-haze px-3 py-1 text-[13px] font-medium text-subtle" data-id="element-48" data-mp="seh4mpiom0mp">
            Past
          </span>}
      </div>

      <div className="mt-5 flex items-start gap-3" data-id="element-49" data-mp="1287rjpz5u30a">
        <CalendarDays className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-50" data-mp="178lni9nhlmhy" />
        <h4 id={`session-${session.id}-date`} className="font-heading text-[22px] font-bold leading-tight tracking-tight text-ink sm:text-[24px]" data-id="element-51" data-mp="1c1etf0uoz0vm">
          {dateLabel}
        </h4>
      </div>

      <dl className="mt-6 space-y-4" data-id="element-52" data-mp="1l2mprddndrn2">
        <div className="flex items-center gap-3" data-id="element-53" data-mp="1pzmvfn6l4sdn">
          <Clock className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-54" data-mp="1q0ekef5ofruy" />
          <dt className="sr-only" data-id="element-55" data-mp="3m42nwuvra2b3">Time</dt>
          <dd className="text-[16px] text-body" data-id="element-56" data-mp="3m0omppytvz58">{session.timeLabel}</dd>
        </div>
        <div className="flex items-center gap-3" data-id="element-57" data-mp="3h5re2k5gnrsv">
          <User className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-58" data-mp="3vt45gphy8rr6" />
          <dt className="sr-only" data-id="element-59" data-mp="3r2467dlcxxli">Presenter</dt>
          <dd className="break-words text-[16px] font-medium text-ink" data-id="element-60" data-mp="3r1ceiduvsk0i">
            {session.presenter}
          </dd>
        </div>
        <div className="flex items-center gap-3" data-id="element-61" data-mp="8pze6az9g0b2">
          <Video className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-62" data-mp="4lhvyg8jtv2e" />
          <dt className="sr-only" data-id="element-63" data-mp="imub4pfvxsn8">Format</dt>
          <dd className="text-[16px] text-body" data-id="element-64" data-mp="ilsp5pxbzlqq">Live on Zoom</dd>
        </div>
      </dl>

      <div className="mt-auto pt-9" data-id="element-65" data-mp="doj0ichrne3y">
        {isPast ? <Button size="lg" variant="outline" className="h-[50px] w-full rounded-full border-hairline text-[15px] font-semibold text-subtle" disabled aria-label={`Registration closed for the ${dateLabel} session`} data-id="element-66" data-mp="2kdfl85w2y6u2">
            Registration closed
          </Button> : <Button asChild size="lg" className="btn-gradient h-[50px] w-full rounded-full text-base font-medium text-white shadow-control transition-shadow hover:shadow-card" data-id="element-67" data-mp="zuv86emf5phe">
            <a href={session.registrationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap" data-id="element-68" data-mp="q3h3lsfm1zhe">
              Register
              <span className="sr-only" data-id="element-69" data-mp="29fonlm36ch2">
                for the {dateLabel} session with {session.presenter} (opens in a
                new tab)
              </span>
            </a>
          </Button>}
      </div>
    </article>;
}